import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AlertTriangle, CheckCircle2, Mail } from 'lucide-react';
import { Loader } from '@/components/loader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { AccountCard } from './account-card';
import { getApiErrorMessage } from './api-result';
import { buildPrimaryChangeFailureNotice, buildPrimaryChangeNotice } from './primary-change-notice';
import { EMAIL_SCHEMA } from './schemas';
import { useChangePrimaryEmail, useUsersByEmail } from './user-emails.hooks';
import type { Notice, PrimaryChangeAttempt, Refresh } from './types';

const SEARCH_SCHEMA = z.object({ email: EMAIL_SCHEMA });

/**
 * Back-office dialog for ROOT staff: finds accounts by any of their emails, then adds, removes or
 * promotes an address. Every write asks for a reason and is audited on the server.
 */
export default function ManageEmailsDialog() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [searchedEmail, setSearchedEmail] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);

  const form = useForm<z.infer<typeof SEARCH_SCHEMA>>({
    resolver: zodResolver(SEARCH_SCHEMA),
    defaultValues: { email: '' },
  });

  const changePrimary = useChangePrimaryEmail();
  const {
    data: users,
    error,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useUsersByEmail(searchedEmail);

  useEffect(() => {
    if (!error) return;
    toast({
      title: 'Search Failed',
      description: getApiErrorMessage(error, 'Could not look up that email'),
      variant: 'destructive',
    });
  }, [error, toast]);

  const search = ({ email, nextNotice }: { email: string; nextNotice: Notice | null }) => {
    setNotice(nextNotice);
    form.setValue('email', email);
    if (email === searchedEmail) refetch();
    else setSearchedEmail(email);
  };

  const handleChanged = ({ email, notice: message }: Refresh) =>
    search({ email, nextNotice: message });

  const retryPrimaryChange = async (attempt: PrimaryChangeAttempt) => {
    try {
      const result = await changePrimary.mutateAsync({ ...attempt, dryRun: false });
      search({
        email: attempt.email,
        nextNotice: buildPrimaryChangeNotice({ result, attempt }),
      });
    } catch (retryError) {
      setNotice(buildPrimaryChangeFailureNotice({ error: retryError, attempt }));
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchedEmail('');
      setNotice(null);
      form.reset();
    }
    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="ml-3 mb-3" variant="outline">
          <Mail className="mr-2 h-4 w-4" />
          Manage Emails
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage user emails</DialogTitle>
          <DialogDescription>
            Find an account by any of its emails, then add, remove or promote an address. Changes
            apply to every organization the user belongs to.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ email }) => search({ email, nextNotice: null }))}
            className="space-y-1">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Email</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input placeholder="name@hospital.org" autoComplete="off" {...field} />
                    </FormControl>
                    <Button type="submit" disabled={isFetching}>
                      Find
                    </Button>
                  </div>
                  <FormDescription>
                    Matches primary and secondary emails, including deleted accounts.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {notice && (
          <Alert variant={notice.severity === 'warning' ? 'warning' : 'default'}>
            {notice.severity === 'warning' ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <AlertDescription>
              {notice.message}
              {notice.retry && (
                <div className="mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={changePrimary.isPending}
                    onClick={() => retryPrimaryChange(notice.retry as PrimaryChangeAttempt)}>
                    {changePrimary.isPending ? 'Retrying...' : 'Retry'}
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isLoading || isFetching ? (
          <div className="flex items-center justify-center py-6">
            <Loader />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Couldn&apos;t load the account. Search again to see its current emails.
            </AlertDescription>
          </Alert>
        ) : (
          users && (
            <div className="space-y-4">
              {users.length === 0 && (
                <p className="py-3 text-muted-foreground">No account uses {searchedEmail}.</p>
              )}
              {users.map(user => (
                <AccountCard
                  key={user.id}
                  user={user}
                  onChanged={handleChanged}
                  onNotice={setNotice}
                />
              ))}
            </div>
          )
        )}

        <p className="border-t pt-3 text-xs text-muted-foreground">
          Every change is recorded in the audit log with your name and the ticket you give.
        </p>
      </DialogContent>
    </Dialog>
  );
}
